import { useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Page } from '../../components/layout/AppShell'
import { PageHeader } from '../../components/layout/PageHeader'
import { Badge, Panel, Spinner, ErrorState, Table, type Column } from '../../components/ui'
import { useAgent } from './useAgents'

const BUILTIN_TOOLS = new Set(['Bash', 'Read', 'Write', 'Edit', 'Glob', 'Grep', 'TodoWrite'])

type Entry = { name: string; kind: 'tool' | 'skill' | 'builtin'; path: string | null; href: string | null }

function toolEntry(name: string): Entry {
  if (BUILTIN_TOOLS.has(name)) return { name, kind: 'builtin', path: null, href: null }
  const path = `tools/${name}.py`
  return { name, kind: 'tool', path, href: null }
}

function skillEntry(name: string): Entry {
  const path = `skills/${name}/`
  return { name, kind: 'skill', path, href: null }
}

export function AgentDetailPage() {
  const { agentName } = useParams({ from: '/app/agents/$agentName' })
  const navigate = useNavigate()
  const { data: agent, isLoading, isError } = useAgent(agentName)

  const agentsCrumb = { label: 'Agents', onClick: () => navigate({ to: '/agents', search: { tab: 'agents' } }) }

  if (isLoading) {
    return (
      <Page width="wide" header={<PageHeader crumbs={[agentsCrumb, { label: agentName }]} />}>
        <Spinner />
      </Page>
    )
  }
  if (isError || !agent) {
    return (
      <Page width="wide" header={<PageHeader crumbs={[agentsCrumb, { label: agentName }]} />}>
        <ErrorState message="Couldn't load this agent." />
      </Page>
    )
  }

  const rows: { field: string; value: string }[] = [
    { field: 'Model', value: agent.model },
    { field: 'Run mode', value: agent.run_mode },
    { field: 'User skills', value: agent.userskills ? 'Enabled' : 'Disabled' },
    { field: 'Effort', value: agent.effort || 'default' },
    { field: 'Max turns', value: String(agent.max_turns) },
    { field: 'Source file', value: agent.file },
  ]
  const cols: Column<{ field: string; value: string }>[] = [
    { key: 'field', header: 'Field', render: (r) => <span className="font-medium">{r.field}</span> },
    { key: 'value', header: 'Value', render: (r) => <span className="font-mono text-xs">{r.value}</span> },
  ]

  const tools = agent.tool_allowlist.map(toolEntry)
  const skills = agent.skill_allowlist.map(skillEntry)

  return (
    <Page
      width="wide"
      header={
        <PageHeader
          crumbs={[agentsCrumb, { label: agent.name }]}
          actions={<Badge tone="indigo">{agent.run_mode}</Badge>}
        />
      }
    >
      <Panel title="Definition">
        <Table columns={cols} rows={rows} />
      </Panel>
      <Panel title="System prompt">
        <SystemPrompt prompt={agent.prompt} />
      </Panel>
      <Panel title={`Tool allowlist (${tools.length})`}>
        <Allowlist entries={tools} />
      </Panel>
      <Panel title={`Skill allowlist (${skills.length})`}>
        <Allowlist entries={skills} />
      </Panel>
    </Page>
  )
}

const PROMPT_LINE_CAP = 10

function SystemPrompt({ prompt }: { prompt: string }) {
  const [expanded, setExpanded] = useState(false)
  if (!prompt) return <p className="text-sm text-text-muted">None</p>
  const lines = prompt.split('\n')
  const truncated = lines.length > PROMPT_LINE_CAP
  const shown = expanded || !truncated ? prompt : lines.slice(0, PROMPT_LINE_CAP).join('\n')
  return (
    <div className="flex flex-col gap-2">
      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-gray-700">{shown}</pre>
      {truncated && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="self-start text-sm text-indigo-600 hover:underline"
        >
          {expanded ? 'Show less' : `Read more (${lines.length - PROMPT_LINE_CAP} more lines)`}
        </button>
      )}
    </div>
  )
}

function Allowlist({ entries }: { entries: Entry[] }) {
  if (entries.length === 0) return <p className="px-1 py-6 text-center text-sm text-text-muted">None</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-text-muted">
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Kind</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <EntryRow key={e.name} entry={e} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function EntryRow({ entry: e }: { entry: Entry }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <tr
        onClick={() => setOpen(!open)}
        className="cursor-pointer border-b border-gray-200 last:border-0 hover:bg-gray-50"
      >
        <td className="px-3 py-2">
          <span className="flex items-center gap-2 font-mono text-xs">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {e.name}
          </span>
        </td>
        <td className="px-3 py-2">
          <Badge tone={e.kind === 'builtin' ? 'neutral' : 'indigo'}>{e.kind}</Badge>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-gray-200 last:border-0 bg-gray-50/50">
          <td colSpan={2} className="px-3 py-3">
            {e.kind === 'builtin' ? (
              <p className="text-sm text-text-muted">Claude Code builtin tool — no repository file.</p>
            ) : (
              <div className="flex flex-col gap-1 text-sm">
                <span className="font-mono text-xs text-text-muted">{e.path}</span>
                <span className="text-xs text-text-muted">Workspace repository path</span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}
